import { EServicePriceType } from '@irembo-andela/irembogov3-common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Condition {
  attribute: string;
  comparator: string;
  value: string;
  attributeType: string;
}

export interface PricingScenario {
  key: string;
  value: any;
  valueAttribute: any;
  currency: string;
  conditions: Condition[];
  valueFormula: any;
}

export interface IPricingTypesOption {
  label: string;
  value: EServicePriceType;
}

@Component({
  selector: 'irembogov-configure-service-change-request-price-handler',
  templateUrl:
    './configure-service-change-request-price-handler.component.html',
})
export class ConfigureServiceChangeRequestPriceHandlerComponent
  implements OnInit, OnChanges, OnDestroy
{
  EPricingType = EServicePriceType;
  @Input() label = '';
  @Input() withCurrency = false;
  @Input() isPricingNumberType = false;
  @Input() formFields!: any;
  @Input() defaultData?: any;
  @Input() buttonName!: string;
  @Input() showToggleButton!: boolean;
  @Input() organizationId!: string;
  @Output() valueChange: EventEmitter<Record<string, any>> = new EventEmitter<
    Record<string, any>
  >();
  selectedPricingType!: EServicePriceType;
  pricingTypes: IPricingTypesOption[] = [];
  scenarioList$ = new BehaviorSubject<PricingScenario[]>([]);
  fixedScenario$ = new BehaviorSubject<any>(null);

  /**
   * Tracks whether `defaultData` has ever been applied. `defaultData` (the
   * service's saved pricing) can arrive asynchronously, after this
   * component's own ngOnInit has already run with it still undefined —
   * without this, Fixed/Dynamic selection and the saved scenarios would be
   * silently stuck on the pre-data defaults forever, since defaultData was
   * only ever read once, in ngOnInit. Applying it once, whenever it first
   * becomes available, also avoids clobbering in-progress edits if the
   * parent's defaultData reference happens to change again later.
   */
  private hasAppliedDefaultData = false;

  ngOnInit() {
    this.initSelectedPricingType();
    this.subscribeToForms();
    this.initializeDefaultData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.hasAppliedDefaultData &&
      changes['defaultData'] &&
      !changes['defaultData'].firstChange &&
      this.defaultData
    ) {
      this.initializeDefaultData();
    }
  }

  ngOnDestroy() {
    this.scenarioList$.unsubscribe();
    this.fixedScenario$.unsubscribe();
  }

  initSelectedPricingType() {
    this.selectedPricingType = this.EPricingType.FIXED;
    this.pricingTypes = [
      { label: 'Fixed', value: this.EPricingType.FIXED },
      { label: 'Dynamic', value: this.EPricingType.DYNAMIC },
    ];

    if (this.isPricingNumberType) {
      this.pricingTypes.push({
        label: 'Price Logic',
        value: this.EPricingType.PRICE_LOGIC,
      });
    }
  }

  subscribeToForms() {
    this.scenarioList$.subscribe(scenarioList => {
      this.valueChange.emit({
        ...{
          priceType: this.selectedPricingType,
          baseValue: this.defaultData?.baseValue,
          baseCurrency: this.defaultData?.baseCurrency,
        },
        dynamicConditions: scenarioList,
      });
    });

    this.fixedScenario$.subscribe(fixedScenario => {
      this.valueChange.emit({
        ...fixedScenario,
        dynamicConditions: this.scenarioList$.value,
      });
    });
  }

  initializeDefaultData() {
    if (this.defaultData) {
      this.hasAppliedDefaultData = true;
      let selectedPricingType =
        this.defaultData?.dynamicConditions.length > 0 && this.showToggleButton
          ? this.EPricingType.DYNAMIC
          : this.EPricingType.FIXED;

      if (this.defaultData.priceType === this.EPricingType.PRICE_LOGIC) {
        selectedPricingType = this.EPricingType.PRICE_LOGIC;
      }

      this.selectedPricingType = selectedPricingType;
      if (selectedPricingType === this.EPricingType.DYNAMIC) {
        this.scenarioList$.next(this.defaultData?.dynamicConditions);
      } else {
        this.fixedScenario$.next({
          priceType: this.selectedPricingType,
          baseValue: this.defaultData?.baseValue,
          baseCurrency: this.defaultData?.baseCurrency,
        });
      }
    }
  }

  onAddScenarios(scenario: any) {
    if (this.selectedPricingType === this.EPricingType.PRICE_LOGIC) {
      // For price logic, we directly emit the complete pricing object
      this.valueChange.emit(scenario);
    } else {
      // For dynamic pricing, we handle scenarios
      this.scenarioList$.next(scenario.scenarios);
    }
  }

  onSetScenario(scenario: any) {
    if (this.selectedPricingType === this.EPricingType.FIXED) {
      scenario['priceType'] = this.selectedPricingType;
      this.fixedScenario$.next(scenario);
    }
  }

  onResetScenarios() {
    this.scenarioList$.next([]);
    this.fixedScenario$.next({});
  }

  onSelectPricingType(pricingType: EServicePriceType) {
    this.selectedPricingType = pricingType;
    this.onResetScenarios();
  }
}
